import type { FC } from "react";
import { memo, useEffect, useMemo } from "react";

import type { SelectOptionType } from "@ui/components/DEPRECATED_CustomSelect";
import { customYearsArray, getNewDaysArray, monthsArray } from "@ui/components/DEPRECATED_CustomSelect";
import { Controller, useFormContext } from "@ui/components/Form";
import { useTranslations } from "@ui/providers/useTranslations";
import { getDaysInMonth } from "@ui/utils/date";

import { useConfig } from "@jti/app-context/useConfig";

import { Select } from "../Inputs";

import styles from "./DateDropdown.module.scss";

export type DateDropdownProps = {
  dropdownFieldPrefix: string;
  fieldName: string;
  yearsRange?: SelectOptionType[];
  isDateChangeRestricted?: boolean;
  isError?: boolean;
  required?: boolean;
};

export const DateDropdown: FC<DateDropdownProps> = memo(
  ({
    fieldName,
    dropdownFieldPrefix,
    yearsRange,
    isDateChangeRestricted = false,
    isError = false,
    required = false,
  }) => {
    const { watch, setValue, register, control } = useFormContext();
    const { __ } = useTranslations();
    const { dobFormType } = useConfig();
    const isReversedDropdowns =
      dobFormType === "dropdowns_empty_reversed" || dobFormType === "dropdowns_filled_reversed";

    const dobDay = watch(`${dropdownFieldPrefix}.dobDay`);
    const dobMonth = watch(`${dropdownFieldPrefix}.dobMonth`);
    const dobYear = watch(`${dropdownFieldPrefix}.dobYear`);

    const selectedYear = dobYear?.value;
    const selectedMonth = dobMonth?.value;
    const selectedDay = dobDay?.value;

    const daysInSelectedMonth = useMemo(() => {
      const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
      const daysArray = getNewDaysArray(daysInMonth);
      if (selectedDay > daysInMonth) {
        setValue(`${dropdownFieldPrefix}.dobDay`, daysArray.at(-1));
      }
      return daysArray;
    }, [dropdownFieldPrefix, selectedDay, selectedMonth, selectedYear, setValue]);

    useEffect(() => {
      const isDobSet = dobDay && dobMonth && dobYear;

      if (isDobSet) {
        setValue(fieldName, `${dobDay?.value}.${dobMonth?.value}.${dobYear?.value}`);
        return;
      }

      setValue(fieldName, "");
    }, [dobDay, dobMonth, dobYear, fieldName, setValue]);

    useEffect(() => {
      register(fieldName);
    }, [register]);

    const dobDropdowns = [
      <Controller
        key="dayDobDropdown"
        control={control}
        defaultValue={dobDay}
        name={`${dropdownFieldPrefix}.dobDay`}
        render={({ field }) => (
          <Select
            {...field}
            inputId="dobDaySelectInput"
            dataTestId="dobDaySelectInput"
            isSearchable={false}
            aria-label={__("auth.age_verification.method_fields.dob_day.select_placeholder")}
            placeholder={__("auth.age_verification.method_fields.dob_day.select_placeholder")}
            options={daysInSelectedMonth}
            isDisabled={isDateChangeRestricted}
            containerClassName={styles.input}
            isError={isError}
            required={required}
          />
        )}
      />,
      <Controller
        key="monthDobDropdown"
        control={control}
        defaultValue={dobMonth}
        name={`${dropdownFieldPrefix}.dobMonth`}
        render={({ field }) => (
          <Select
            {...field}
            inputId="dobMonthSelectInput"
            dataTestId="dobMonthSelectInput"
            isSearchable={false}
            aria-label={__("auth.age_verification.method_fields.dob_month.select_placeholder")}
            placeholder={__("auth.age_verification.method_fields.dob_month.select_placeholder")}
            options={monthsArray(__)}
            isDisabled={isDateChangeRestricted}
            containerClassName={styles.input}
            isError={isError}
            required={required}
          />
        )}
      />,
      <Controller
        key="yearDobDropdown"
        control={control}
        defaultValue={dobYear}
        name={`${dropdownFieldPrefix}.dobYear`}
        render={({ field }) => (
          <Select
            {...field}
            inputId="dobYearSelectInput"
            dataTestId="dobYearSelectInput"
            isSearchable={false}
            aria-label={__("auth.age_verification.method_fields.dob_year.select_placeholder")}
            placeholder={__("auth.age_verification.method_fields.dob_year.select_placeholder")}
            options={yearsRange || customYearsArray(10, 10)}
            isDisabled={isDateChangeRestricted}
            containerClassName={styles.input}
            isError={isError}
            required={required}
          />
        )}
      />,
    ];

    return (
      <div className={styles.dobSelectBlock}>{isReversedDropdowns ? dobDropdowns.toReversed() : dobDropdowns}</div>
    );
  }
);
